import { States } from '@/helpers/common/states';
import OrderSite from '@/model/OrderSite';
import Page from '@/model/Page';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDialog } from '@/hooks/useDialog';
import validators from '@/helpers/validators';
import useForm from '@/hooks/useForm';
import api, { AxiosError } from '@/services/api';
import { toast } from 'react-toastify';
import { CancelTicketsUI, ShouldShowModal } from './ui';
import { FormInputFilterEvent } from '../../types';

export const CancelTicketsScreen: React.FC = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [currentPage, setCurrentPage] = useState<Page<OrderSite, OrderSite>>({
    page: 1,
    pageSize: 10,
    sort: 'orderDate',
    order: 'DESC',
  });
  const [state, setState] = useState<States>(States.default);
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(ShouldShowModal.filter);
  const { title, visible, onChangeTitle, onToggle } = useDialog();

  const {
    formData: formDataFilter,
    formErrors: formErrorsFilter,
    onChangeFormInput: onChangeFormInputFilter,
    isFormValid: isFormValidFilter,
    resetForm: resetFormFilter,
  } = useForm({
    initialData: {
      filterSearch: '',
      inputSearch: '',
    },
    validators: {
      filterSearch: [validators.required],
      inputSearch: [validators.required],
    },
  });

  const handleOnPage = async (page: Page<OrderSite, OrderSite>): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.post<Page<OrderSite, OrderSite>>(`/order/${id}/orders`, page);
      if (data) {
        setCurrentPage(data);
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status !== 401) {
        toast.error(err.message);
      }
    } finally {
      setState(States.default);
    }
  };
  const handleClearFilter = async (): Promise<void> => {
    resetFormFilter();
    const newPage: Page<OrderSite, OrderSite> = {
      page: 1,
      pageSize: 10,
      sort: 'orderDate',
      order: 'ASC',
    };
    onToggle();
    await handleOnPage(newPage);
  };

  const handleOnPagination = (pageNumber: number): void => {
    const newPage: Page<OrderSite, OrderSite> = {
      ...currentPage,
      page: pageNumber,
    };
    handleOnPage(newPage);
  };

  const handleGoToCancel = async (orderId: string | null): Promise<void> => {
    if (!orderId) {
      toast.error('ID do pedido inválido.');
      return;
    }
    try {
      setState(States.loading);
      const response = await api.patch(`/order/${id}/cancel/${orderId}`);
      if (response.status === 200) {
        handleClearFilter();
        toast.success('Pedido cancelado com sucesso!');
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status !== 400 && err.response?.status !== 401) {
        toast.error(err.message);
      }
    } finally {
      setState(States.default);
    }
  };

  const handleOnFilter = async (): Promise<void> => {
    if (!isFormValidFilter()) return;

    const entity = {} as OrderSite;
    const filter = formDataFilter[FormInputFilterEvent.filterSearch];
    const value = formDataFilter[FormInputFilterEvent.inputSearch];

    if (filter === 'orderItemId') {
      entity.events = [{ items: [{ id: String(value) } as any] } as any];
    } else if (filter === 'itemLabel') {
      entity.events = [
        {
          items: [{ itemLabel: String(value) } as any],
        } as any,
      ];
    } else if (filter === 'totalValue') {
      const normalized = String(value).replace(/\./g, '').replace(',', '.');
      entity.totalValue = Number(normalized);
    } else if (filter === 'orderDate') {
      const [day, month, year] = String(value).split('/');
      entity.orderDate = new Date(Number(year), Number(month) - 1, Number(day));
    }

    const newPage: Page<OrderSite, OrderSite> = {
      page: 1,
      pageSize: 10,
      entity,
      sort: 'orderDate',
      order: 'DESC',
    };

    onToggle();
    await handleOnPage(newPage);
  };

  const handleOnShouldShowModal = async ({
    value,
    newTitleModal,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
  }): Promise<void> => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    onToggle();
  };

  useEffect(() => {
    handleOnPage(currentPage);
  }, []);

  return (
    <CancelTicketsUI
      state={state}
      title={title}
      visible={visible}
      shouldShowModal={shouldShowModal}
      onShouldShowModal={handleOnShouldShowModal}
      onToggle={onToggle}
      currentPage={currentPage}
      formData={formDataFilter}
      onChangeFormInput={onChangeFormInputFilter}
      formErrors={formErrorsFilter}
      clearFilter={handleClearFilter}
      onPagination={handleOnPagination}
      onFilter={handleOnFilter}
      cancelTicket={handleGoToCancel}
    />
  );
};
